package com.schoolconnect.app.feature.documents

import com.schoolconnect.app.feature.attendance.ParentAttendanceChild
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ParentChildSelectionStore
import com.schoolconnect.app.feature.common.DebouncePolicy
import com.schoolconnect.app.repository.documents.DocumentLibraryRepository
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.repository.files.appendMimeTypeExtension
import com.schoolconnect.app.repository.files.mimeTypeForDocumentType
import com.schoolconnect.app.repository.systemlookup.SystemLookupRepository
import com.schoolconnect.app.downloadFile
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class ParentDocumentLibraryUiState(
    val children: List<ParentAttendanceChild> = emptyList(),
    val selectedChildIndex: Int = 0,
    val documents: List<com.schoolconnect.app.datasource.network.response.DocumentResponseDTO> = emptyList(),
    val totalCount: Int = 0,
    val query: String = "",
    val category: String = "All Categories",
    val pageIndex: Int = 0,
    val pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
    val sortField: DocumentLibrarySortField = DocumentLibrarySortField.UPDATED_ON,
    val sortAscending: Boolean = false,
    val isLoading: Boolean = true,
    val errorMessage: String = "",
    val downloadError: String = "",
    val categoryOptions: List<DocumentLibraryLookupOption> = emptyList(),
    val isLoadingLookupOptions: Boolean = false,
    val lookupOptionsError: String = ""
) {
    val selectedChild: ParentAttendanceChild? get() = children.getOrNull(selectedChildIndex)
}

class ParentDocumentLibraryViewModel(initialChildren: List<ParentAttendanceChild>) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var loadJob: Job? = null
    private val _uiState = MutableStateFlow(ParentDocumentLibraryUiState(children = initialChildren))
    val uiState: StateFlow<ParentDocumentLibraryUiState> = _uiState.asStateFlow()

    init {
        scope.launch {
            ParentChildSelectionStore.selectedChildId.collect { childId ->
                val index = _uiState.value.children.indexOfFirst { it.id == childId }
                if (index >= 0 && index != _uiState.value.selectedChildIndex) selectChild(index)
            }
        }
        load()
        loadLookupOptions()
    }

    fun selectChild(index: Int) {
        val safeIndex = index.coerceIn(0, _uiState.value.children.lastIndex.coerceAtLeast(0))
        _uiState.update { it.copy(selectedChildIndex = safeIndex, pageIndex = 0, isLoading = true) }
        _uiState.value.selectedChild?.id?.let(ParentChildSelectionStore::select)
        load()
    }

    fun queryChanged(value: String) { _uiState.update { it.copy(query = value, pageIndex = 0) }; load(debounce = true) }
    fun categoryChanged(value: String) { _uiState.update { it.copy(category = value, pageIndex = 0) }; load() }
    fun pageChanged(value: Int) { _uiState.update { it.copy(pageIndex = value.coerceAtLeast(0)) }; load() }
    fun pageSizeChanged(value: Int) { _uiState.update { it.copy(pageSize = value.coerceIn(10, 50), pageIndex = 0) }; load() }
    fun sortChanged(field: DocumentLibrarySortField) {
        _uiState.update { current -> current.copy(sortField = field, sortAscending = if (current.sortField == field) !current.sortAscending else true, pageIndex = 0) }
        load()
    }

    fun loadDocumentDetails(documentId: String, onComplete: (DocumentLibraryRow) -> Unit) {
        val studentId = _uiState.value.selectedChild?.id.orEmpty()
        if (documentId.isBlank() || studentId.isBlank()) return
        scope.launch {
            DocumentLibraryRepository.getParentDocument(documentId, studentId)
                .onSuccess { document ->
                    // Ignore a late response if the parent switched to another child while it loaded.
                    if (_uiState.value.selectedChild?.id == studentId) {
                        onComplete(document.toDocumentLibraryRow())
                    }
                }
        }
    }

    fun download(documentId: String) {
        val studentId = _uiState.value.selectedChild?.id.orEmpty()
        if (studentId.isBlank()) return
        _uiState.update { it.copy(downloadError = "") }
        scope.launch {
            runCatching {
                // The parent list response is intentionally compact and does not include file metadata.
                // Load the full record so the download has the original filename and MIME type.
                val document = DocumentLibraryRepository.getParentDocument(documentId, studentId).getOrThrow()
                val url = DocumentLibraryRepository.getParentDocumentDownloadUrl(documentId, studentId).getOrThrow()
                val bytes = FileUploadRepository.downloadBytes(url).getOrThrow()
                val metadata = document.fileId?.trim()?.takeIf { it.isNotBlank() }
                    ?.let { FileUploadRepository.getMetadata(it).getOrNull() }
                val mimeType = metadata?.declaredMimeType?.trim()?.takeIf { it.isNotBlank() }
                    ?: metadata?.detectedMimeType?.trim()?.takeIf { it.isNotBlank() }
                    ?: mimeTypeForDocumentType(document.fileType.orEmpty())
                val fallbackName = document.docId?.takeIf { it.isNotBlank() } ?: documentId
                val filename = appendMimeTypeExtension(
                    document.fileName?.trim()?.takeIf { it.isNotBlank() } ?: "document-$fallbackName",
                    mimeType
                )
                check(downloadFile(filename, bytes, mimeType)) { "File downloads are not supported on this platform." }
            }.onFailure { error ->
                _uiState.update { it.copy(downloadError = error.message ?: "Unable to download document.") }
            }
        }
    }

    fun clear() = scope.cancel()

    fun retryLookupOptions() = loadLookupOptions(forceRemote = true)

    private fun load(debounce: Boolean = false) {
        loadJob?.cancel()
        val current = _uiState.value
        val studentId = current.selectedChild?.id.orEmpty()
        if (studentId.isBlank()) {
            _uiState.update { it.copy(documents = emptyList(), totalCount = 0, isLoading = false, errorMessage = "No linked child was found.") }
            return
        }
        _uiState.update { it.copy(isLoading = true, errorMessage = "") }
        loadJob = scope.launch {
            if (debounce) delay(DebouncePolicy.DEFAULT_DELAY_MS)
            val categoryCode = if (current.category.isBlank() || current.category.equals("All Categories", ignoreCase = true)) {
                null
            } else {
                current.categoryOptions.firstOrNull { option ->
                    option.label.equals(current.category.trim(), ignoreCase = true) || option.code.equals(current.category.trim(), ignoreCase = true)
                }?.code
            }
            if (current.category.isNotBlank() && !current.category.equals("All Categories", ignoreCase = true) && categoryCode == null) {
                _uiState.update { it.copy(isLoading = false, errorMessage = "Document lookup values are unavailable. Please refresh and try again.") }
                return@launch
            }
            DocumentLibraryRepository.loadParentPage(studentId, current.query, categoryCode, current.pageIndex, current.pageSize, current.sortField.apiField, if (current.sortAscending) "asc" else "desc")
                .onSuccess { response ->
                    val count = response.count
                    val items = response.items
                    if (count == null || count < 0 || items == null) {
                        _uiState.update {
                            it.copy(isLoading = false, errorMessage = "The server returned an incomplete document list. Please retry.")
                        }
                    } else {
                        _uiState.update { it.copy(documents = items, totalCount = count, isLoading = false, errorMessage = "") }
                    }
                }
                .onFailure { error -> _uiState.update { it.copy(isLoading = false, errorMessage = error.message ?: "Unable to load documents.") } }
        }
    }

    private fun loadLookupOptions(forceRemote: Boolean = false) {
        _uiState.update { it.copy(isLoadingLookupOptions = true, lookupOptionsError = "") }
        scope.launch {
            SystemLookupRepository.refresh(forceRemote = forceRemote)
                .onSuccess { snapshot ->
                    val lookupItems = snapshot.activeValues("DOCUMENT_CATEGORY")
                    val categories = lookupItems.mapNotNull { item ->
                        val code = item.code?.trim().orEmpty()
                        val label = item.displayValue?.trim().orEmpty()
                        code.takeIf { it.isNotBlank() && label.isNotBlank() }
                            ?.let { DocumentLibraryLookupOption(it, label) }
                    }
                    if (categories.isEmpty() || categories.size != lookupItems.size) {
                        _uiState.update {
                            it.copy(
                                categoryOptions = emptyList(),
                                isLoadingLookupOptions = false,
                                lookupOptionsError = "Document category options are missing a code or display label. Retry loading them."
                            )
                        }
                    } else {
                        _uiState.update { it.copy(categoryOptions = categories, isLoadingLookupOptions = false, lookupOptionsError = "") }
                    }
                }
                .onFailure { throwable ->
                    _uiState.update { it.copy(isLoadingLookupOptions = false, lookupOptionsError = throwable.message ?: "Unable to load document lookup values.") }
                }
        }
    }
}
